import {
    Dispatch,
    SetStateAction,
    ReactNode,
    useCallback,
    useMemo,
    useState,
    createContext,
    useContext,
    useEffect,
    useRef,
} from 'react'
import { useSnack } from './snack_context'
import { useLoading } from './loading_context'
import { createCoin, listCoins, updateCoin } from '../services/coin'
import { useNavigate } from 'react-router-dom'
import { useAccount } from './account_context'
import { isLogged } from '../services/auth'

export enum CoinType {
    Main = 1,
    Normal = 2,
    Other = 3,
}

export enum CoinExchangeType {
    Normal = 0,
    Reverse = 1,
}

export interface ExchageInfo {
    buyExchange: number
    saleExchange: number
    flagReverseExchange: number
    coin: CoinInfo
    date: Date
}

export interface CoinInfo {
    id?: number
    name: string
    iso: string
    symbol: string
    pathImage: string
    hideDecimal: boolean
    exchanges: ExchageInfo[]
}

export interface CreateCoinInfo extends CoinInfo {
    step: number
    isEditing: boolean
}

export interface CoinErrorInfo {
    name: string
    iso: string
    symbol: string
    buyExchange: string
    saleExchange: string
}

const createCoinInfoDefault: CreateCoinInfo = {
    name: '',
    iso: '',
    symbol: '',
    pathImage: '',
    step: 1,
    isEditing: false,
    hideDecimal: false,
    exchanges: [],
}

const coinErrorDefault: CoinErrorInfo = {
    name: '',
    iso: '',
    symbol: '',
    buyExchange: '',
    saleExchange: '',
}

export interface CoinContextInterface {
    coins: CoinInfo[]
    setCoins: Dispatch<SetStateAction<CoinInfo[]>>
    createCoinInfo: CreateCoinInfo
    setCreateCoinInfo: Dispatch<SetStateAction<CreateCoinInfo>>
    coinErrorInfo: CoinErrorInfo
    setCoinErrorInfo: Dispatch<SetStateAction<CoinErrorInfo>>
    initCreateNewCoin(): void
    editCoin(id: number): Promise<void>
    validate(): boolean
    handleCreateNewCoin(): Promise<void>
    getCoins(): Promise<void>
    setDefaultExchanges(coins: CoinInfo[]): CoinInfo[]
}

const coinContext = createContext<CoinContextInterface>({
    coins: [],
    setCoins: () => {},
    createCoinInfo: createCoinInfoDefault,
    setCreateCoinInfo: () => {},
    coinErrorInfo: coinErrorDefault,
    setCoinErrorInfo: () => {},
    initCreateNewCoin: () => {},
    editCoin: async () => {},
    validate: () => false,
    handleCreateNewCoin: async () => {},
    getCoins: async () => {},
    setDefaultExchanges: () => [],
})

export function useCoin() {
    return useContext(coinContext)
}

interface Props {
    children: ReactNode
}

export const CoinContextProvider = ({ children }: Props) => {
    const loading = useLoading()
    const snack = useSnack()
    const navigate = useNavigate()
    const [coins, setCoins] = useState<CoinInfo[]>([])

    const [createCoinInfo, setCreateCoinInfo] = useState<CreateCoinInfo>(
        createCoinInfoDefault
    )
    const [coinErrorInfo, setCoinErrorInfo] =
        useState<CoinErrorInfo>(coinErrorDefault)
    const intervalRef = useRef<NodeJS.Timeout>()

    useEffect(() => {
        if (!isLogged()) return

        intervalRef.current = setInterval(() => {
            getCoins()
        }, 1000 * 90)

        return () => {
            clearInterval(intervalRef.current)
        }
    }, [])

    const getCoins = useCallback(async () => {
        try {
            const res = await listCoins()
            setCoins(setDefaultExchanges(res))
        } catch (error) {
            snack.connectionFail(error)
        }
    }, [])

    const initCreateNewCoin = useCallback(() => {
        setCreateCoinInfo({ ...createCoinInfoDefault })
    }, [])

    const editCoin = useCallback(
        async (id: number) => {
            const coin = coins.find((c) => c.id === id)

            if (!coin) throw new Error('Coin not found')
            setCreateCoinInfo({ ...coin, isEditing: true, step: 1 })
        },
        [coins]
    )

    const validate = useCallback((): boolean => {
        let isOk = true

        if (!createCoinInfo.name) {
            setCoinErrorInfo((prev) => ({
                ...prev,
                name: 'Campo obrigatório',
            }))
            isOk = false
        }
        if (!createCoinInfo.iso) {
            setCoinErrorInfo((prev) => ({
                ...prev,
                iso: 'Campo obrigatório',
            }))
            isOk = false
        }
        if (!createCoinInfo.symbol) {
            setCoinErrorInfo((prev) => ({
                ...prev,
                symbol: 'Campo obrigatório',
            }))
            isOk = false
        }
        if (!createCoinInfo.pathImage) {
            snack.error('Selecione uma bandeira para a moeda')
            isOk = false
        }

        return isOk
    }, [createCoinInfo])

    const handleCreateNewCoin = useCallback(async () => {
        loading.show()
        try {
            if (!createCoinInfo.isEditing)
                await createCoin({
                    nome: createCoinInfo.name,
                    iso: createCoinInfo.iso,
                    simbolo: createCoinInfo.symbol,
                    path_imagem: createCoinInfo.pathImage,
                    flag_esconder_decimal: Number(createCoinInfo.hideDecimal),
                })
            else
                await updateCoin({
                    id_moeda: createCoinInfo.id as number,
                    nome: createCoinInfo.name,
                    iso: createCoinInfo.iso,
                    simbolo: createCoinInfo.symbol,
                    path_imagem: createCoinInfo.pathImage,
                    flag_esconder_decimal: Number(createCoinInfo.hideDecimal),
                })
            await getCoins()
            snack.success(
                `Moeda ${
                    !createCoinInfo.isEditing ? 'criada' : 'atualizada'
                } com sucesso!`
            )
            navigate('/system/coin')
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.hide()
    }, [createCoinInfo, getCoins, navigate, loading])

    const setDefaultExchanges = useCallback(function (
        coinsParam: CoinInfo[]
    ): CoinInfo[] {
        const date = new Date()
        for (const coin of coinsParam) {
            for (const otherCoin of coinsParam.filter(
                (c) => c.id !== coin.id
            )) {
                if (!coin.exchanges.some((e) => e.coin.id === otherCoin.id))
                    coin.exchanges.push({
                        buyExchange: 0,
                        saleExchange: 0,
                        flagReverseExchange: 0,
                        coin: {
                            ...otherCoin,
                            exchanges: [],
                        },
                        date,
                    })
            }
        }
        return coinsParam
    }, [])

    const value = useMemo(
        () => ({
            coins,
            setCoins,
            createCoinInfo,
            setCreateCoinInfo,
            coinErrorInfo,
            setCoinErrorInfo,
            initCreateNewCoin,
            editCoin,
            validate,
            handleCreateNewCoin,
            getCoins,
            setDefaultExchanges,
        }),
        [
            coins,
            setCoins,
            createCoinInfo,
            setCreateCoinInfo,
            coinErrorInfo,
            setCoinErrorInfo,
            initCreateNewCoin,
            editCoin,
            validate,
            handleCreateNewCoin,
            getCoins,
            setDefaultExchanges,
        ]
    )

    return <coinContext.Provider value={value}>{children}</coinContext.Provider>
}
